/* =============================================================================
   UTILS / DSFinV-K-EXPORT — Digitale Schnittstelle Finanzbehörden (Außenprüfung)
   =============================================================================
   Liefert ein ZIP mit den Pflicht-CSV-Dateien gemäß DSFinV-K-Taxonomie (BMF
   2020/2024). Das Format ist Bestandteil der KassenSichV (§ 4 Abs. 1 KassSichV)
   und wird vom Außenprüfer als digitale Schnittstelle verlangt — separat vom
   DATEV-Light-CSV für den Steuerberater.

   ─── Was im ZIP steckt ────────────────────────────────────────────────────────
   Stammdaten:
     - index.xml                Metabeschreibung aller Files (vereinfacht)
     - cashregister.csv         Kassen-Stammdaten (Brand/Modell/Seriennr/SW)
     - location.csv             Standort des Restaurants (Adresse, USt-IdNr)
     - vat.csv                  Steuersatz-Kennzeichnung (7% / 19% / 0%)
     - pa.csv                   Bezahlmethoden (Bar/Karte/Stripe/Unbezahlt)
   Bewegungsdaten:
     - cashpointclosing.csv     Z-Bon-Header pro Geschäftstag im Zeitraum
     - transactions.csv         Geschäftsvorfälle (eine Zeile pro Bon)
     - lines.csv                Positionen (eine Zeile pro Order-Item)
     - tse.csv                  TSE-Signatur-Daten (eine Zeile pro signiertem
                                Bon, gespeist aus tse_audit_log)

   ─── Pseudo-Schlüssel ─────────────────────────────────────────────────────────
   Alle Files führen das Triple (Z_KASSE_ID, Z_ERSTELLUNG, Z_NR) als
   Fremdschlüssel auf cashpointclosing. Konvention:
     Z_KASSE_ID    = Restaurant-ID (z.B. "rest_default") — eindeutig pro Kasse
     Z_ERSTELLUNG = ISO-Timestamp der ZIP-Generierung (für alle gleichen Wert)
     Z_NR         = laufende Nummer pro Geschäftstag im Zeitraum, startet bei 1
                    am ersten Bestelltag und wächst pro Tag mit Bestellungen.

   Korrekturbelege (is_correction=true) erhalten BON_TYP='Beleg' mit
   BON_STORNO=1 und negativen UMS_BRUTTO-Werten, plus referenced_transaction_id
   in BON_NOTIZ als 'STORNO_VON:<originalBonId>'.

   ─── Encoding ─────────────────────────────────────────────────────────────────
   UTF-8 OHNE BOM, Semikolon-Separator, CRLF-Zeilenende (Standard für deutsche
   Verwaltung — exakt wie DATEV-Light-CSV, aber ohne BOM weil DSFinV-K das nicht
   vorsieht und manche Prüfer-Tools mit BOM stolpern).

   ─── Aufruf ──────────────────────────────────────────────────────────────────
     await exportDsfinvk({ from: '2026-05-01', to: '2026-05-31', label: 'mai-2026' });
   Lädt orders + items + tse-Audit-Log per Storage-Adapter, baut die Files,
   verpackt sie via window.JSZip in ein ZIP und triggert den Browser-Download.
   ============================================================================ */

import { showToast, todayISO, getRestaurantTimezone, DEFAULT_TIMEZONE } from './helpers.js';

/* ---------------------------------------------------------------------------- */
/* Hilfsfunktionen                                                              */
/* ---------------------------------------------------------------------------- */

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}

/** CSV-Field nach DSFinV-K-Konvention escapen.
 *  Enthält das Feld ", ; oder \r/\n → in "..." einrahmen, innere " → "".
 *  null/undefined → leerer String. */
function csv(s) {
  const v = s == null ? '' : String(s);
  if (/[";\r\n]/.test(v)) return '"' + v.replace(/"/g, '""') + '"';
  return v;
}

/** Cents → "12.34" (DSFinV-K verlangt PUNKT als Dezimaltrennzeichen,
 *  KEIN Tausender-Separator). Vorzeichen wird beibehalten für Stornos. */
function money(cents) {
  const c = Number(cents) || 0;
  const sign = c < 0 ? '-' : '';
  const abs  = Math.abs(c);
  const euros = Math.floor(abs / 100);
  const rest  = abs % 100;
  return sign + String(euros) + '.' + String(rest).padStart(2, '0');
}

/** ISO-Timestamp → 'YYYY-MM-DDTHH:MM:SS' (DSFinV-K verbietet Millisekunden +
 *  Timezone-Suffix in den meisten Feldern).
 *
 *  Phase 28 (S26): TZ-aware — projiziert auf die konfigurierte Restaurant-
 *  Timezone (Default Europe/Berlin), nicht mehr UTC. Hintergrund:
 *  GoBD/KassenSichV erwartet deutsche Lokalzeit als „lokale Zeit" — UTC
 *  würde Tagesgrenzen in Z_ERSTELLUNG verschieben und Außenprüfer-Tools
 *  irritieren. DSFinV-K Anlage I erlaubt explizit lokale Zeit ohne
 *  Timezone-Suffix.
 *
 *  Implementation: en-CA + en-GB → exakt ISO-Format 'YYYY-MM-DDTHH:MM:SS'. */
function isoNoMs(iso, tz) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const zone = tz || getRestaurantTimezone() || DEFAULT_TIMEZONE;
  const dateFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit'
  });
  const timeFmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
  });
  const dateStr = dateFmt.format(d).replace(/[‐-―]/g, '-');
  let timeStr = timeFmt.format(d);
  // en-GB liefert '24:00:00' bei Mitternacht — normalisieren auf '00:00:00'.
  if (timeStr.startsWith('24:')) timeStr = '00:' + timeStr.slice(3);
  return dateStr + 'T' + timeStr;
}

/** "YYYY-MM-DD" + "HH:MM" → ISO-Timestamp ohne ms.
 *  Wird genutzt um pickup_date+pickup_time als Bon-Start zu rekonstruieren,
 *  wenn kein expliziter created_at vorliegt. */
function combineDateTime(date, time) {
  if (!date) return '';
  const t = (time && time.length >= 5) ? time.slice(0, 5) : '12:00';
  return date + 'T' + t + ':00';
}

function joinLines(rows) {
  // CRLF-Zeilenenden gemäß DSFinV-K-Konvention (üblich für deutsche Verwaltung)
  return rows.join('\r\n') + '\r\n';
}

/** Zwei-stellige Jahreszahl-Pad-Helper für Z_NR (z.B. 001, 002, ...) */
function padNr(n, width = 4) {
  return String(n).padStart(width, '0');
}

/* ---------------------------------------------------------------------------- */
/* Stammdaten-Konstanten                                                        */
/* ---------------------------------------------------------------------------- */

const TAXONOMIE_VERSION = '2.3';
const KASSE_BRAND       = 'Grill-and-Green';
const KASSE_MODELL      = 'Cockpit-Cloud';
const KASSE_SW_BRAND    = 'Grill-and-Green-Cockpit';
const KASSE_BASISWAEH   = 'EUR';

/** Mapping G&G-payment_method → DSFinV-K ZAHLART_TYP.
 *  Erlaubte Typen: Bar | Unbar | ECKarte | Kreditkarte | ElZahlungsdienstleister | Guthabenkarte | Keine. */
const PAYMENT_TYPE_MAP = {
  cash:    'Bar',
  card:    'ECKarte',
  stripe:  'ElZahlungsdienstleister',
  unpaid:  'Keine'
};

/** USt-Schlüssel nach DSFinV-K Anlage I: 1=19% Standard, 2=7% ermäßigt, 5=0% steuerfrei. */
function vatKey(rate) {
  const r = Number(rate);
  if (r === 19) return '1';
  if (r === 7)  return '2';
  return '5';
}

/* ---------------------------------------------------------------------------- */
/* Datensammlung                                                                */
/* ---------------------------------------------------------------------------- */

async function loadExportData(fromIso, toIso) {
  const storage = window.App && window.App.storage;
  if (!storage || !storage.orders) {
    throw new Error('Storage-Adapter nicht verfügbar.');
  }
  if (typeof storage.orders.listForRange !== 'function') {
    throw new Error('storage.orders.listForRange fehlt — alten Storage-Adapter erkannt.');
  }

  const orders = await storage.orders.listForRange(fromIso, toIso);
  const ids = orders.map(o => o.id);
  const items = await storage.orders.listItemsForOrderIds(ids);
  // S137 — Teilbelege (order_receipts) sind eigene Kassenbelege (Split).
  let receipts = [];
  if (typeof storage.orders.listReceiptsForRange === 'function') {
    try {
      receipts = await storage.orders.listReceiptsForRange(fromIso, toIso);
    } catch (e) {
      console.warn('[dsfinvk] order_receipts nicht lesbar:', e.message || e);
    }
  }
  // S242/phase_173 — Teil-Storno-Belege sind eigene Kassenbelege mit eigener
  // Belegnummer und TSE-Signatur. Bis S241 fehlten sie im DSFinV-K komplett,
  // weil der Export nur Korrektur-ORDERS (is_correction) kannte.
  let correctionBatches = [];
  let corrections = [];
  if (typeof storage.orders.listCorrectionBatchesForRange === 'function') {
    try {
      correctionBatches = await storage.orders.listCorrectionBatchesForRange(fromIso, toIso);
      if (correctionBatches.length && typeof storage.orders.listCorrectionsForBatchIds === 'function') {
        corrections = await storage.orders.listCorrectionsForBatchIds(correctionBatches.map(b => b.id));
      }
    } catch (e) {
      console.warn('[dsfinvk] order_correction_batches nicht lesbar:', e.message || e);
    }
  }
  return { orders, items, receipts, correctionBatches, corrections };
}

/**
 * S137 — Fiskal-Belege für DSFinV-K aus Orders + Teilbelegen ableiten.
 * Ein Beleg ist genau EINE signierte Kassentransaktion:
 *   • Voll-Zahlung: orders-Zeile MIT invoice_number (mark_order_paid, TSE auf der Order)
 *   • Teilbeleg:    order_receipts-Zeile (Split, eigene invoice_number + payment_method + TSE)
 *   • Storno:       Korrektur-Order (is_correction → Stornobeleg-Nr, negative Beträge)
 * Strukturell KEINE Belege (fallen raus): offene/unbezahlte Orders, Schwund-Orders
 * (invoice NULL, Ware abgeschrieben — kein Umsatz, kein TSE) und die Split-Mutter-Order
 * (invoice NULL; ihr Umsatz lebt in den Teilbelegen). „Ohne Beleg kein Bonjournal-Eintrag."
 * Jeder Beleg ist order-shaped (damit buildDayClosures/-Transactions ihn 1:1 nehmen) +
 * trägt `_belegItems` (für lines.csv) + `_belegKind`.
 */
export function deriveBelege(orders, receipts, itemsByOrder, correctionBatches, corrections) {
  const ord    = Array.isArray(orders) ? orders : [];
  const rcpts  = Array.isArray(receipts) ? receipts : [];
  const batches = Array.isArray(correctionBatches) ? correctionBatches : [];
  const corrs   = Array.isArray(corrections) ? corrections : [];
  const itemsBy = itemsByOrder || {};
  const ordersById = {};
  for (const o of ord) ordersById[o.id] = o;

  const belege = [];

  // Voll-Zahlungen (kein Split, kein Storno, kein Abbruch). Items = alle (receipt_id NULL).
  for (const o of ord) {
    if (!o.invoiceNumber || o.isCorrection) continue;
    if (o.status === 'cancelled' || o.status === 'no_show') continue;
    belege.push(Object.assign({}, o, { _belegItems: itemsBy[o.id] || [], _belegKind: 'order' }));
  }

  // Teilbelege — order-shaped normalisiert; Items = nur die von DIESEM Beleg gedeckten.
  for (const r of rcpts) {
    const parent = ordersById[r.orderId] || null;
    const items  = (itemsBy[r.orderId] || []).filter(it => it.receiptId === r.id);
    belege.push({
      id:               r.id,
      invoiceNumber:    r.invoiceNumber,
      orderNumber:      parent ? parent.orderNumber : r.orderId,
      pickupDate:       parent ? parent.pickupDate : (r.createdAt || '').slice(0, 10),
      pickupTime:       parent ? parent.pickupTime : '',
      totalGrossCents:  r.totalGrossCents,
      subtotalNetCents: r.subtotalNetCents,
      tax7Cents:        r.tax7AmountCents,
      tax19Cents:       r.tax19AmountCents,
      paymentMethod:    r.paymentMethod,
      paymentStatus:    'paid',
      status:           'picked_up',           // ein bezahlter Teilbeleg ist abgeschlossen
      isCorrection:     false,
      createdAt:        r.createdAt,
      paidAt:           r.createdAt,
      updatedAt:        r.createdAt,
      customerName:     parent ? parent.customerName : '',
      specialRequests:  '',
      cancelledBy:      null,
      tseTransactionId:    r.tseTransactionId,
      tseSignatureCounter: r.tseSignatureCounter,
      tseSignatureValue:   r.tseSignatureValue,
      tseSignedAt:         r.tseSignedAt,
      tseQrPayload:        r.tseQrPayload,
      tseMode:             r.tseMode,
      _belegItems:      items,
      _belegKind:       'receipt'
    });
  }

  // Korrekturbelege (Voll-Storno) — unverändert, negative Beträge.
  for (const o of ord) {
    if (!o.isCorrection) continue;
    belege.push(Object.assign({}, o, { _belegItems: itemsBy[o.id] || [], _belegKind: 'correction' }));
  }

  // S242/phase_173 — Teil-Storno-Belege. Order-shaped wie die Teilbelege, mit
  // NEGATIVEN Beträgen (so liegen sie in der DB) und den stornierten Positionen
  // als Zeilen. `kind='schwund'` kommt hier gar nicht erst an (der Loader
  // filtert auf refund) — Warenverlust ist kein Beleg.
  const corrsByBatch = {};
  for (const c of corrs) {
    const bid = c.batch_id || c.batchId;
    if (!bid) continue;
    (corrsByBatch[bid] = corrsByBatch[bid] || []).push(c);
  }
  for (const b of batches) {
    const parent = ordersById[b.orderId] || null;
    const parentItems = itemsBy[b.orderId] || [];
    const lines = (corrsByBatch[b.id] || []).map((c, i) => {
      const src = parentItems.find(it => it.id === (c.order_item_id || c.orderItemId)) || {};
      const qty = Number(c.qty_cancelled || c.qtyCancelled) || 1;
      const gross = Number(c.refund_amount_cents || c.refundAmountCents) || 0;
      return {
        id:             c.id,
        lineIndex:      src.lineIndex != null ? src.lineIndex : i,
        menuItemId:     src.menuItemId || '',
        nameSnapshot:   'Storno: ' + (src.nameSnapshot || src.name || '—'),
        categorySnapshot: src.categorySnapshot || '',
        qty,
        unitPriceCents: -Math.round(gross / qty),
        lineTotalCents: -gross,           // Storno-Zeile mindert
        taxRate:        Number(c.tax_rate || c.taxRate) || 7
      };
    });
    belege.push({
      id:               b.id,
      invoiceNumber:    b.invoiceNumber,
      orderNumber:      parent ? parent.orderNumber + '-PK' : b.orderId,
      pickupDate:       parent ? parent.pickupDate : String(b.createdAt || '').slice(0, 10),
      pickupTime:       parent ? parent.pickupTime : '',
      totalGrossCents:  b.totalGrossCents,
      subtotalNetCents: b.subtotalNetCents,
      tax7Cents:        b.tax7Cents,
      tax19Cents:       b.tax19Cents,
      paymentMethod:    b.paymentMethod,
      paymentStatus:    'refunded',
      status:           'picked_up',
      isCorrection:     true,
      correctsOrderId:  b.orderId,
      correctionReasonCode: b.reasonCategory,
      createdAt:        b.createdAt,
      paidAt:           b.createdAt,
      updatedAt:        b.createdAt,
      customerName:     parent ? parent.customerName : '',
      specialRequests:  '',
      cancelledBy:      b.createdBy || null,
      tseTransactionId:    b.tseTransactionId,
      tseSignatureCounter: b.tseSignatureCounter,
      tseSignatureValue:   b.tseSignatureValue,
      tseSignedAt:         b.tseSignedAt,
      tseQrPayload:        b.tseQrPayload,
      tseMode:             b.tseMode,
      _belegItems:      lines,
      _belegKind:       'correction_batch'
    });
  }

  return belege;
}

/* ---------------------------------------------------------------------------- */
/* Z_NR pro Tag                                                                  */
/* ---------------------------------------------------------------------------- */

/** Bildet eine Map pickupDate → Z_NR (1..n) in chronologischer Reihenfolge,
 *  nur für Tage, an denen es überhaupt productive ODER Korrektur-orders gab. */
function buildDayClosures(orders) {
  const byDate = {};
  for (const o of orders) {
    if (o.status === 'cancelled' && !o.isCorrection) continue; // reine Stornos vor Bezahlung zählen nicht als Tagesabschluss
    if (o.status === 'no_show') continue;
    const d = o.pickupDate;
    if (!d) continue;
    if (!byDate[d]) byDate[d] = { orders: [], grossCents: 0, byMethodCents: {} };
    byDate[d].orders.push(o);
    byDate[d].grossCents += Number(o.totalGrossCents) || 0;
    const m = (o.paymentStatus === 'paid' || o.paymentStatus === 'refunded')
              ? (o.paymentMethod || 'unpaid')
              : 'unpaid';
    byDate[d].byMethodCents[m] = (byDate[d].byMethodCents[m] || 0) + (Number(o.totalGrossCents) || 0);
  }
  const dates = Object.keys(byDate).sort();
  const closures = dates.map((d, i) => ({
    date: d,
    zNr: i + 1,
    grossCents: byDate[d].grossCents,
    byMethodCents: byDate[d].byMethodCents,
    orders: byDate[d].orders
  }));
  return closures;
}

/* ---------------------------------------------------------------------------- */
/* CSV-Builder pro Datei                                                        */
/* ---------------------------------------------------------------------------- */

function buildCashregisterCsv(kasseId, erstellung, closures, settings) {
  const header = [
    'Z_KASSE_ID','Z_ERSTELLUNG','Z_NR',
    'KASSE_BRAND','KASSE_MODELL','KASSE_SERIENNR',
    'KASSE_SW_BRAND','KASSE_SW_VERSION',
    'KASSE_BASISWAEH_CODE','KEINE_UST_ZUORDNUNG'
  ].map(csv).join(';');
  const swVersion = settings && settings.appVersion ? settings.appVersion : '0.26.0-beta';
  const rows = closures.map(c => [
    csv(kasseId), csv(erstellung), csv(padNr(c.zNr)),
    csv(KASSE_BRAND), csv(KASSE_MODELL), csv(kasseId),
    csv(KASSE_SW_BRAND), csv(swVersion),
    csv(KASSE_BASISWAEH), csv('0')
  ].join(';'));
  return joinLines([header, ...rows]);
}

function buildLocationCsv(kasseId, erstellung, closures, settings) {
  const header = [
    'Z_KASSE_ID','Z_ERSTELLUNG','Z_NR',
    'LOC_NAME','LOC_STRASSE','LOC_PLZ','LOC_ORT','LOC_LAND','LOC_USTID'
  ].map(csv).join(';');
  const r = (settings && settings.restaurant) || {};
  const name   = (settings && settings.restaurantName) || 'Grill & Green';
  const ustId  = r.ustId || '';
  const rows = closures.map(c => [
    csv(kasseId), csv(erstellung), csv(padNr(c.zNr)),
    csv(name), csv(r.street || ''), csv(r.zip || ''), csv(r.city || ''),
    csv('DEU'), csv(ustId)
  ].join(';'));
  return joinLines([header, ...rows]);
}

function buildVatCsv(kasseId, erstellung, closures) {
  const header = [
    'Z_KASSE_ID','Z_ERSTELLUNG','Z_NR',
    'UST_SCHLUESSEL','UST_SATZ','UST_BESCHR'
  ].map(csv).join(';');
  const rates = [
    { key: '1', rate: '19.00', desc: 'Allgemeiner Steuersatz' },
    { key: '2', rate: '7.00',  desc: 'Ermäßigter Steuersatz' },
    { key: '5', rate: '0.00',  desc: 'Nicht steuerbar' }
  ];
  const rows = [];
  for (const c of closures) {
    for (const v of rates) {
      rows.push([
        csv(kasseId), csv(erstellung), csv(padNr(c.zNr)),
        csv(v.key), csv(v.rate), csv(v.desc)
      ].join(';'));
    }
  }
  return joinLines([header, ...rows]);
}

function buildPaCsv(kasseId, erstellung, closures) {
  const header = [
    'Z_KASSE_ID','Z_ERSTELLUNG','Z_NR',
    'ZAHLART_TYP','ZAHLART_NAME','ZAHLART_WAEH'
  ].map(csv).join(';');
  const rows = [];
  const seen = new Set();
  for (const c of closures) {
    for (const method of Object.keys(c.byMethodCents)) {
      const key = c.zNr + '|' + method;
      if (seen.has(key)) continue;
      seen.add(key);
      const typ = PAYMENT_TYPE_MAP[method] || 'Unbar';
      rows.push([
        csv(kasseId), csv(erstellung), csv(padNr(c.zNr)),
        csv(typ), csv(method), csv('EUR')
      ].join(';'));
    }
  }
  return joinLines([header, ...rows]);
}

function buildCashpointclosingCsv(kasseId, erstellung, closures, settings) {
  const header = [
    'Z_KASSE_ID','Z_ERSTELLUNG','Z_NR','Z_BUCHUNGSTAG',
    'TAXONOMIE_VERSION','Z_START_ID','Z_ENDE_ID',
    'Z_SE_ZAHLUNGEN','Z_SE_BARZAHLUNGEN'
  ].map(csv).join(';');
  const rows = closures.map(c => {
    const inv = c.orders.map(o => o.invoiceNumber).filter(Boolean).sort();
    const cashGross = c.byMethodCents.cash || 0;
    return [
      csv(kasseId), csv(erstellung), csv(padNr(c.zNr)), csv(c.date),
      csv(TAXONOMIE_VERSION),
      csv(inv[0] || ''), csv(inv[inv.length - 1] || ''),
      csv(money(c.grossCents)),
      csv(money(cashGross))
    ].join(';');
  });
  return joinLines([header, ...rows]);
}

function buildTransactionsCsv(kasseId, erstellung, closures) {
  const header = [
    'Z_KASSE_ID','Z_ERSTELLUNG','Z_NR','BON_ID','BON_NR','BON_TYP','BON_NAME',
    'TERMINAL_ID','BON_STORNO','BON_START','BON_ENDE',
    'BEDIENER_ID','BEDIENER_NAME','UMS_BRUTTO',
    'KUNDE_NAME','KUNDE_ID','KUNDE_TYP','KUNDE_STRASSE','KUNDE_PLZ',
    'KUNDE_ORT','KUNDE_LAND','KUNDE_USTID','BON_NOTIZ'
  ].map(csv).join(';');
  const rows = [];
  for (const c of closures) {
    for (const o of c.orders) {
      const isStorno = o.isCorrection ? '1' : '0';
      const bonName  = o._belegKind === 'correction_batch' ? 'Teil-Stornobeleg'
                     : o.isCorrection ? 'Stornobeleg'
                     : (o._belegKind === 'receipt' ? 'Teilbeleg' : 'Beleg');
      const bonNotiz = o.isCorrection && o.correctsOrderId
        ? 'STORNO_VON:' + o.correctsOrderId + (o.correctionReasonCode ? ' GRUND:' + o.correctionReasonCode : '')
        : (o.specialRequests || '');
      const bonStart = isoNoMs(o.createdAt) || combineDateTime(o.pickupDate, o.pickupTime);
      const bonEnde  = isoNoMs(o.paidAt) || isoNoMs(o.updatedAt) || bonStart;
      rows.push([
        csv(kasseId), csv(erstellung), csv(padNr(c.zNr)),
        csv(o.id), csv(o.invoiceNumber || o.orderNumber || o.id),
        csv('Beleg'), csv(bonName),
        csv('terminal-1'), csv(isStorno),
        csv(bonStart), csv(bonEnde),
        csv(o.cancelledBy || 'system'), csv('Cockpit'),
        csv(money(o.totalGrossCents)),
        csv(o.customerName || ''), csv(''), csv('Kunde'),
        csv(''), csv(''), csv(''), csv('DEU'), csv(''),
        csv(bonNotiz)
      ].join(';'));
    }
  }
  return joinLines([header, ...rows]);
}

function buildLinesCsv(kasseId, erstellung, closures, itemsByOrder) {
  const header = [
    'Z_KASSE_ID','Z_ERSTELLUNG','Z_NR','BON_ID','POS_ZEILE',
    'GV_TYP','GV_NAME','ARTIKELTEXT','ARTIKELNUMMER','MENGE','FAKTOR','EINHEIT',
    'STNR','UST_SCHLUESSEL','PREIS','BRUTTO','NETTO','UST'
  ].map(csv).join(';');
  const rows = [];
  for (const c of closures) {
    for (const o of c.orders) {
      // S137 — Items kommen vom Beleg selbst (`_belegItems`): Voll-Beleg = alle
      // Order-Items, Teilbeleg = nur die von DIESEM Receipt gedeckten Items.
      const items = o._belegItems || (itemsByOrder && itemsByOrder[o.id]) || [];
      if (items.length === 0) {
        // Fallback: aus preorderSnapshot, falls keine order_items existieren
        const snap = (o.preorderSnapshot && o.preorderSnapshot.items) || [];
        for (let i = 0; i < snap.length; i++) {
          const it = snap[i];
          const rate = Number(it.taxRate || 7);
          const lineBrutto = Number(it.lineTotalCents || (Number(it.unitPriceCents || 0) * Number(it.qty || 1))) || 0;
          const lineNetto  = Math.round(lineBrutto / (1 + rate / 100));
          const lineUst    = lineBrutto - lineNetto;
          rows.push([
            csv(kasseId), csv(erstellung), csv(padNr(c.zNr)),
            csv(o.id), csv(String(i + 1)),
            csv('Umsatz'), csv('Verkauf'),
            csv(it.nameSnapshot || it.name || 'Artikel'),
            csv(it.menuItemId || ''),
            csv(String(it.qty || 1)), csv('1'), csv('Stk'),
            csv(''), csv(vatKey(rate)),
            csv(money(it.unitPriceCents)),
            csv(money(lineBrutto)), csv(money(lineNetto)), csv(money(lineUst))
          ].join(';'));
        }
        continue;
      }
      for (const it of items) {
        const rate = Number(it.taxRate || 7);
        const lineBrutto = Number(it.lineTotalCents) || 0;
        const lineNetto  = Math.round(lineBrutto / (1 + rate / 100));
        const lineUst    = lineBrutto - lineNetto;
        const sign = o.isCorrection ? -1 : 1;
        rows.push([
          csv(kasseId), csv(erstellung), csv(padNr(c.zNr)),
          csv(o.id), csv(String((it.lineIndex || 0) + 1)),
          csv('Umsatz'), csv(o.isCorrection ? 'Storno' : 'Verkauf'),
          csv(it.nameSnapshot || 'Artikel'),
          csv(it.menuItemId || ''),
          csv(String(it.qty || 1)), csv('1'), csv('Stk'),
          csv(''), csv(vatKey(rate)),
          csv(money(it.unitPriceCents * sign)),
          csv(money(lineBrutto)), csv(money(lineNetto)), csv(money(lineUst))
        ].join(';'));
      }
    }
  }
  return joinLines([header, ...rows]);
}

/**
 * S137 — TSE.csv BELEG-basiert: jeder signierte Beleg (Voll-Zahlung, Teilbeleg,
 * Storno) trägt seine TSE-Signatur direkt auf der Beleg-Zeile (orders.tse_* bzw.
 * order_receipts.tse_*). BON_ID = Beleg-ID → ein Split-Teilbeleg landet mit seiner
 * EIGENEN TSE-Transaktion unter seiner eigenen BON_ID, nicht unter der Order.
 * (Frühere audit-log-Quelle hätte alle Receipt-TX unter der Eltern-order_id
 * gebündelt.) Belege ohne Signatur (z.B. mock/disabled) werden übersprungen.
 */
function buildTseCsv(kasseId, erstellung, closures) {
  const header = [
    'Z_KASSE_ID','Z_ERSTELLUNG','Z_NR','BON_ID',
    'TSE_ID','TSE_TANR','TSE_TA_START','TSE_TA_ENDE',
    'TSE_TA_VORGANGSART','TSE_TA_SIGZ','TSE_TA_SIG','TSE_VORGANGSDATEN',
    'TSE_TA_FEHLER','TSE_TA_ERROR'
  ].map(csv).join(';');
  const rows = [];
  for (const c of closures) {
    for (const b of c.orders) {
      if (!b.tseTransactionId) continue; // nur signierte Belege
      const ts = isoNoMs(b.tseSignedAt) || isoNoMs(b.paidAt) || isoNoMs(b.createdAt);
      rows.push([
        csv(kasseId), csv(erstellung), csv(padNr(c.zNr)),
        csv(b.id),
        csv(b.tseMode || ''),
        csv(b.tseTransactionId || ''),
        csv(ts), csv(ts),
        csv(b.isCorrection ? 'Kassenbeleg-V1-Storno' : 'Kassenbeleg-V1'),
        csv(b.tseSignatureCounter != null ? String(b.tseSignatureCounter) : ''),
        csv(b.tseSignatureValue || ''),
        csv(b.tseQrPayload ? String(b.tseQrPayload).slice(0, 1000) : ''),
        csv('0'),
        csv('')
      ].join(';'));
    }
  }
  return joinLines([header, ...rows]);
}

/** Vereinfachtes index.xml — listet alle CSVs als <Table>-Elemente mit ihren
 *  Spalten. Echte DSFinV-K-index.xml ist deutlich umfangreicher (per Datei
 *  inkl. Datentyp-Schemas), das hier ist ein pragmatisches Minimum, das
 *  Außenprüfern als Inhaltsverzeichnis dient. */
function buildIndexXml(files) {
  const xml = [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<DataSet xmlns="urn:dsfinvk:2.3" version="2.3">',
    '  <DataSupplier>',
    '    <Name>Grill &amp; Green Cockpit</Name>',
    '    <Location>Online</Location>',
    '    <Comment>Pragmatische DSFinV-K-Light-Implementierung, Vollformat siehe Spec v2.3</Comment>',
    '  </DataSupplier>',
    '  <Media>',
    '    <Name>cashpointclosing</Name>',
    files.map(f => '    <Table>' + f + '</Table>').join('\n'),
    '  </Media>',
    '</DataSet>'
  ];
  return xml.join('\n') + '\n';
}

/* ---------------------------------------------------------------------------- */
/* Public API                                                                   */
/* ---------------------------------------------------------------------------- */

/**
 * Baut das DSFinV-K-ZIP und triggert den Browser-Download.
 * @param {Object} opts { from: ISO-Date, to: ISO-Date, label?: string }
 * @returns {Promise<{ files: string[], filename: string } | { rows: 0, filename: null }>}
 */
export async function exportDsfinvk(opts) {
  const o = opts || {};
  const today = todayISO();
  const from = o.from || today;
  const to   = o.to   || from;
  const label = o.label || (from === to ? 'tag-' + from : 'zeitraum-' + from + '_' + to);

  // JSZip-Verfügbarkeit prüfen (CDN-Skript im index.html)
  const JSZip = window.JSZip;
  if (typeof JSZip !== 'function') {
    showToast('JSZip nicht geladen — bitte Seite neu laden.', 'error');
    throw new Error('JSZip nicht verfügbar (window.JSZip fehlt)');
  }

  const state = getState();
  const settings = state.settings || {};
  const kasseId = (state.currentRestaurantId)
                  || (window.App && window.App.currentRestaurantId)
                  || 'rest_default';
  const erstellung = isoNoMs(new Date().toISOString());

  // Datensammlung
  showToast('DSFinV-K: lade Daten…', 'info');
  const { orders, items, receipts, correctionBatches, corrections } = await loadExportData(from, to);

  if (!orders || orders.length === 0) {
    showToast('Keine Bestellungen im Zeitraum — kein DSFinV-K-Export erzeugt.', 'info');
    return { rows: 0, filename: null };
  }

  // Items nach order_id gruppieren (für Beleg-Items + lines.csv).
  const itemsByOrder = {};
  for (const it of items) {
    if (!itemsByOrder[it.orderId]) itemsByOrder[it.orderId] = [];
    itemsByOrder[it.orderId].push(it);
  }

  // S137 — fiskalische Belege ableiten (Voll-Zahlungen ∪ Teilbelege ∪ Storni);
  // offene/Schwund/Split-Mutter-Orders fallen strukturell raus. Closures + alle
  // CSV-Builder arbeiten ab hier ausschließlich auf Belegen.
  const belege = deriveBelege(orders, receipts, itemsByOrder, correctionBatches, corrections);
  const closures = buildDayClosures(belege);
  if (closures.length === 0) {
    showToast('Keine Belege im Zeitraum — kein DSFinV-K-Export erzeugt.', 'info');
    return { rows: 0, filename: null };
  }

  // CSV-Files generieren
  const files = {
    'index.xml':              buildIndexXml(['cashregister.csv','location.csv','vat.csv','pa.csv','cashpointclosing.csv','transactions.csv','lines.csv','tse.csv']),
    'cashregister.csv':       buildCashregisterCsv(kasseId, erstellung, closures, settings),
    'location.csv':           buildLocationCsv(kasseId, erstellung, closures, settings),
    'vat.csv':                buildVatCsv(kasseId, erstellung, closures),
    'pa.csv':                 buildPaCsv(kasseId, erstellung, closures),
    'cashpointclosing.csv':   buildCashpointclosingCsv(kasseId, erstellung, closures, settings),
    'transactions.csv':       buildTransactionsCsv(kasseId, erstellung, closures),
    'lines.csv':              buildLinesCsv(kasseId, erstellung, closures),
    'tse.csv':                buildTseCsv(kasseId, erstellung, closures)
  };

  // ZIP bauen
  const zip = new JSZip();
  for (const name of Object.keys(files)) {
    zip.file(name, files[name]);
  }
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });

  // Download triggern
  const filename = `gg-dsfinvk-${label}.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  showToast(
    `DSFinV-K-Export bereit: ${closures.length} Tag${closures.length === 1 ? '' : 'e'}, ` +
    `${orders.length} Bestellung${orders.length === 1 ? '' : 'en'}.`,
    'success'
  );
  return { files: Object.keys(files), filename };
}

/** Komfort-Wrapper für Monat-Export aus dem Tagesabschluss-Tab.
 *  Phase 28 (S26): keine `new Date().toISOString()`-Local→UTC-Falle mehr —
 *  Monatsgrenzen werden direkt als ISO-Strings konstruiert (TZ-immun). */
export async function exportDsfinvkForMonth(year, month) {
  // month ist 0-basiert (wie JavaScript). Letzter Tag eines Monats wird über
  // Tag 0 des Folgemonats berechnet — funktioniert auch über Jahresgrenze.
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const mm = String(month + 1).padStart(2, '0');
  const fromIso = `${year}-${mm}-01`;
  const toIso   = `${year}-${mm}-${String(lastDay).padStart(2, '0')}`;
  const monthLabels = ['januar','februar','maerz','april','mai','juni','juli','august','september','oktober','november','dezember'];
  const label = 'monat-' + monthLabels[month] + '-' + year;
  return exportDsfinvk({ from: fromIso, to: toIso, label });
}

/** Komfort-Wrapper für Jahres-Export. */
export async function exportDsfinvkForYear(year) {
  const fromIso = year + '-01-01';
  const toIso   = year + '-12-31';
  return exportDsfinvk({ from: fromIso, to: toIso, label: 'jahr-' + year });
}

/* Test-Hooks (Phase 27, S25): Pure-Function-Helper als named exports verfügbar
   machen, damit Vitest sie direkt testen kann. Laufzeitverhalten bleibt
   unverändert — keine internen Aufrufstellen ändern sich. Memory-Lesson
   `feedback_gg_hard_way`: additive Exports statt Test-Tricks. */
export {
  csv,
  money,
  isoNoMs,
  combineDateTime,
  joinLines,
  padNr,
  vatKey,
  buildDayClosures
};

// Backward-Compat: Inline-onclick-Handler im day-close-Tab nutzt window.*
if (typeof window !== 'undefined') {
  Object.assign(window, {
    exportDsfinvk,
    exportDsfinvkForMonth,
    exportDsfinvkForYear
  });
}
